import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Form, Input, Select, DatePicker, TimePicker, Button, Card, Row, Col,
    Switch, Upload, InputNumber, Checkbox, Tag, Avatar, Divider, Space,
    Badge, Modal, Spin, ConfigProvider, Tooltip, Radio, message, Alert, List, Descriptions
} from 'antd';
import {
    UserOutlined, CarOutlined, EnvironmentOutlined, CheckCircleOutlined,
    PlusOutlined, DeleteOutlined, UploadOutlined, TeamOutlined, SafetyOutlined,
    ExclamationCircleOutlined, FilePdfOutlined, StarFilled, StarOutlined
} from '@ant-design/icons';
import { recupererLesContratsActifs, recupererLesEntitesDeconstat } from '../../hooks/useHooks';
import { formaterLesContrats, formaterLesEntites, getEnginsFromContrat } from '../../variables/functions';
import frFR from "antd/locale/fr_FR";
import Footer from '../../components/Footer';
import NavBar from '../../components/NavBar';
import SideBar from '../../components/SideBar';
import dayjs from 'dayjs';
import axiosInstance from '../../variables/api/axiosInstance';
import { normalizeParametersOptions, estGarantieRC } from '../../variables/stringFunctions';
import axiosMultiPartDataInstance from '../../variables/api/axiosMultiPartDatainstance';
import { useNavigate } from 'react-router-dom';
import DTRFFormat from '../production/components/docs/DTRFFormat';
import AttestationMoto from '../production/components/docs/AttestationMoto';
import PreviewFicheDeclaration from './components/declaration/PreviewFicheDeclaration';
import { SINISTER_COLORS } from '../../components/ui/colors';
import { displayNumbers } from '../../variables/numbersFunctions';
import VictimesSection, { buildAutoVictimes } from './components/declaration/VictimesSection';
import { TIP_INCONNU } from './components/declaration/declarationTips';
import { rightsService } from '../../services/rightsService';
import { saveDraft, loadDraft, clearDraft, tempsEcoule } from '../../utils/formDraft';

const { Option } = Select;
const { TextArea } = Input;

// Brouillon local de la déclaration (récupération après coupure réseau / rechargement).
const DRAFT_KEY = 'poolins:declaration-sinistre:brouillon';
const DRAFT_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 jours

const TAG_DOMMAGE_COLOR = { CORPOREL: 'red', MIXTE: 'orange', MATERIEL: 'blue', AUCUN: 'default' };

const DeclareSinistre = () => {
    const [form] = Form.useForm();
    // Saisie de la date de déclaration réservée à l'habilitation HSI100 (déclaration antidatée).
    // Sans ce droit, le champ est masqué et le backend retient la date de saisie, comme avant.
    const peutSaisirDateDeclaration = rightsService('HSI100');
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [isAssureConducteur, setIsAssureConducteur] = useState(true);

    // Adversaires
    const [adversaires, setAdversaires] = useState([]);
    const [adversairePrincipalId, setAdversairePrincipalId] = useState(null);

    // Victimes manuelles (les auto sont calculées via useMemo)
    const [victimes, setVictimes] = useState([]);

    const [typeSinistre, setTypeSinistre] = useState('MATERIEL');
    const [selectedGaranties, setSelectedGaranties] = useState([]);
    const [garanties, setGaranties] = useState([]);
    // Évaluation d'ouverture VENTILÉE par garantie : { garantieCodeId: montant }. La RC reçoit la
    // valeur auto-calculée (barème), les autres garanties reçoivent un montant libre.
    const [evalParGarantie, setEvalParGarantie] = useState({});

    // Une garantie est-elle la RC ? (code 'RC' ou variantes libellé — cf. estGarantieRC).
    const isGarantieRC = (g) => (g?.code === 'RC') || estGarantieRC(g?.label) || estGarantieRC(g?.code);

    // Payload par garantie + total (= évaluation d'ouverture globale envoyée au backend).
    const evaluationsGarantiesPayload = useMemo(
        () => (selectedGaranties || []).map((cid) => {
            const g = garanties.find((x) => x.value === cid);
            // `value` = NOM de la garantie ici ; le vrai codeId est sur g.garantie.codeId (résolu au back).
            return { garantieCodeId: g?.garantie?.codeId || null, garantieNom: g?.label || cid, montant: Number(evalParGarantie[cid]) || 0 };
        }),
        [selectedGaranties, garanties, evalParGarantie]
    );
    const totalEvaluation = useMemo(
        () => evaluationsGarantiesPayload.reduce((s, e) => s + (Number(e.montant) || 0), 0),
        [evaluationsGarantiesPayload]
    );
    // Le total ventilé alimente le champ (caché) evaluationInitiale → cohérence aperçu / soumission.
    useEffect(() => {
        form.setFieldsValue({ evaluationInitiale: totalEvaluation });
    }, [totalEvaluation, form]);

    // Contrats / Engins
    const [entites, setEntites] = useState([]);
    const [contratsLoading, setContratsLoading] = useState(false);
    const [contratsData, setContratsData] = useState([]);
    const [engins, setEngins] = useState([]);
    const [selectedContratObj, setSelectedContratObj] = useState(null);
    // Périodes de garantie de TOUTES les versions (avenants) du contrat sélectionné —
    // pour accepter un sinistre tardif tombant dans un avenant antérieur.
    const [periodesCouverture, setPeriodesCouverture] = useState([]);
    // Contrats valides de la même immatriculation dans d'AUTRES compagnies (indicatif, non bloquant).
    const [autresCompagnies, setAutresCompagnies] = useState([]);

    // Divers
    const [loading, setLoading] = useState(false);
    const [theContract, setTheContract] = useState('');
    const [cModal, setCModal] = useState(false);
    const [cityOptions, setCityOptions] = useState([]);
    const [quartiersData, setQuartiersData] = useState([]);
    const [communesData, setCommunesData] = useState([]);
    const [loadingQuartiers, setLoadingQuartiers] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [newQuartierName, setNewQuartierName] = useState(null);
    const [quartierSearch, setQuartierSearch] = useState('');
    const [sequenceSinistre, setSequenceSinistre] = useState('');
    const [assuranceOptions, setAssuranceOptions] = useState([]);
    const [professions, setProfessions] = useState([]);

    // Modal adversaire
    const [showAdversaireModal, setShowAdversaireModal] = useState(false);
    const [adversaireForm] = Form.useForm();
    const [typeAdversaire, setTypeAdversaire] = useState('VEHICULE');
    const [typeSerie, setTypeSerie] = useState('TG');
    const [typeAdversaireDommage, setTypeAdversaireDommage] = useState(null);
    const [isAdversaireConducteur, setIsAdversaireConducteur] = useState(true);

    const navigate = useNavigate();

    // Auto-victimes calculées dynamiquement à partir des dommages renseignés
    const autoVictimes = useMemo(
        () => buildAutoVictimes(formData, isAssureConducteur, adversaires, selectedContratObj),
        [formData, isAssureConducteur, adversaires, selectedContratObj]
    );

    // ── Chargements initiaux ───────────────────────────────────────────────
    const searchContrats = async (searchValue) => {
        setContratsLoading(true);
        try {
            const response = await recupererLesContratsActifs(searchValue);
            setContratsData(formaterLesContrats(response.data.content));
        } catch { setContratsData([]); }
        finally { setContratsLoading(false); }
    };

    // Recherche débouncée (350 ms) pour le Select : évite une requête serveur à chaque frappe.
    const searchTimerRef = useRef(null);
    const debouncedSearchContrats = (value) => {
        setContratsLoading(true);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => searchContrats(value), 350);
    };

    useEffect(() => {
        searchContrats('');
        recupererLesEntitesDeconstat().then(r => setEntites(formaterLesEntites(r.data))).catch(() => setEntites([]));

        axiosInstance.get('/api/parametre/type/VILLE').then(res => {
            const opts = (res.data || []).map(item => {
                const v = item?.libelleParametre || item?.label || item?.value || '';
                if (!v) return null;
                return { value: v.trim(), label: v.trim() };
            }).filter(Boolean).sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
            setCityOptions(opts);
        }).catch(() => { });

        axiosInstance.get('/api/organisme/assurance').then(res => {
            const opts = (Array.isArray(res.data) ? res.data : [])
                .map(it => ({ value: it?.raisonSociale, label: it?.raisonSociale }))
                .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
            setAssuranceOptions(opts);
        }).catch(() => setAssuranceOptions([]));

        axiosInstance.get('/api/parametre/type/PROFESSION').then(res => setProfessions(normalizeParametersOptions(res.data))).catch(() => { });

        loadLocations();
    }, []);

    useEffect(() => {
        // Aperçu PAR CONTRAT : applique le garde-fou catégorie (contrat Pool) côté serveur, donc
        // affiche la bonne série même si l'engin a été reclassé (MOTO au lieu de GUM).
        const cid = formData?.contratCodeId;
        if (!cid) return;
        axiosInstance.get(`/api/sinistre/next-sequence-contrat/${cid}`)
            .then(res => setSequenceSinistre(res.data))
            .catch((err) => {
                console.error("next-sequence error", err);
                setSequenceSinistre('');
            });
    }, [formData?.contratCodeId]);

    useEffect(() => {
        if (!formData?.contratCodeId) return;
        axiosInstance.get(`/api/contrat/preview/${formData.contratCodeId}`).then(response => {
            const engine = response?.data?.engins?.find(e => e.immatriculation === formData?.enginId);
            const g = [];
            engine?.compulsoryGuaranties?.forEach(ga => {
                if (!ga?.nom.toLowerCase().startsWith('surprime')) {
                    g.push({ value: ga.nom, label: ga.nom, garantie: ga, code: ga.nom.startsWith('RC') ? 'RC' : formatGarantieCode(ga.nom) });
                }
            });
            setGaranties(g);
        }).catch((err) => {
            const data = err?.response?.data;
            if (data?.message) {
                message.error(data.message);
            } else {
                message.error("Impossible de charger les garanties de cet engin (vérifiez la configuration de la catégorie).");
            }
            setGaranties([]);
        });
    }, [formData?.contratCodeId]);

    // ── Localisation ───────────────────────────────────────────────────────
    const loadLocations = async () => {
        setLoadingQuartiers(true);
        try {
            const [r1, r2] = await Promise.all([fetch('/data/regions.json'), fetch('/data/communes.json')]);
            const regionsData = await r1.json();
            const communesQuartiersData = await r2.json();
            const communesIndex = new Map();
            regionsData.forEach(region => {
                region.prefectures?.forEach(prefecture => {
                    prefecture.communes?.forEach(commune => {
                        communesIndex.set(commune.id, { id: commune.id, nom: commune.nom, prefecture: { id: prefecture.id, nom: prefecture.nom }, region: { id: region.id, code: region.code, nom: region.nom } });
                    });
                });
            });
            const allQuartiers = [], allCommunes = [];
            communesQuartiersData.forEach(commune => {
                const cwr = communesIndex.get(commune.id);
                if (cwr) {
                    allCommunes.push(cwr);
                    commune.quartiers?.forEach(q => allQuartiers.push({ id: q.id, nom: q.nom, commune: cwr }));
                }
            });
            setQuartiersData(allQuartiers);
            setCommunesData(allCommunes);
        } catch { setQuartiersData([]); setCommunesData([]); }
        finally { setLoadingQuartiers(false); }
    };

    const handleQuartierSelect = (value) => {
        // Quartier inexistant saisi librement : on amorce la création et on exige la commune de rattachement.
        if (typeof value === 'string' && value.startsWith('NEW::')) {
            const nom = value.slice(5).trim();
            setNewQuartierName(nom);
            setQuartierSearch('');
            setSelectedLocation({ type: 'nouveau-quartier', quartier: { nom, isNew: true }, commune: null, prefecture: null, region: null });
            form.setFieldsValue({ quartier: value, lieuSinistre: nom, commune: undefined });
            return;
        }
        setNewQuartierName(null);
        const q = quartiersData.find(x => x.id === value);
        if (q) {
            setSelectedLocation({ type: 'quartier', quartier: q, commune: q.commune, prefecture: q.commune?.prefecture, region: q.commune?.region });
            form.setFieldsValue({ lieuSinistre: q.nom, quartier: q.nom, commune: undefined });
        }
    };

    const handleCommuneSelect = (id) => {
        const c = communesData.find(x => x.id === id);
        if (!c) return;
        // En mode nouveau quartier, la commune sert de rattachement géographique (et non de remplacement).
        if (newQuartierName) {
            setSelectedLocation({ type: 'nouveau-quartier', quartier: { nom: newQuartierName, isNew: true, communeId: c.id }, commune: c, prefecture: c.prefecture, region: c.region });
            form.setFieldsValue({ commune: c.id, lieuSinistre: form.getFieldValue('lieuSinistre') || newQuartierName });
            return;
        }
        setSelectedLocation({ type: 'commune', commune: c, prefecture: c.prefecture, region: c?.region });
        form.setFieldsValue({ lieuSinistre: c.nom, commune: c.nom, quartier: undefined });
    };

    const formatGarantieCode = (value) => {
        if (!value) return '';
        value = value.trim();
        if (value.length <= 3) return value;
        return value.split(/\s+/).map(w => w[0].toUpperCase()).join('');
    };

    const listeDesCategoriesDePermis = ['AM', 'A1', 'A2', 'A', 'B', 'BE', 'C1', 'C', 'D1', 'D'].map(v => ({ value: v, label: v }));

    // ── Navigation ─────────────────────────────────────────────────────────
    // Pré-remplissage de la date de déclaration à la date du jour (comportement habituel).
    // L'utilisateur habilité peut la reculer ; laissée à aujourd'hui, le backend conserve
    // l'heure réelle de saisie.
    useEffect(() => {
        if (peutSaisirDateDeclaration && !form.getFieldValue('dateDeclaration')) {
            form.setFieldsValue({ dateDeclaration: dayjs() });
        }
    }, [peutSaisirDateDeclaration, form]);

    const next = async () => {
        try {
            const values = await form.validateFields();
            setFormData({ ...formData, ...values });
            setCurrentStep(s => s + 1);
        } catch { /* validation */ }
    };
    const prev = () => setCurrentStep(s => s - 1);

    const handleSubmit = () => {
        Modal.confirm({
            title: 'Confirmer la déclaration de sinistre',
            icon: <ExclamationCircleOutlined />,
            content: 'Êtes-vous sûr de vouloir enregistrer cette déclaration ?',
            okText: 'Confirmer', cancelText: 'Annuler',
            onOk: saveSinistre,
        });
    };

    const saveSinistre = () => {
        setLoading(true);
        const fd = new FormData();
        const photos = formData?.photos;
        const croquis = formData?.croquis;
        const positionsAdversaires = formData?.positionVehiculesAdversaires || {};
        const dataObject = {
            ...formData,
            adversaires: adversaires.map(a => ({
                ...a,
                estPrincipal: a.id === adversairePrincipalId,
                positionActuelle: positionsAdversaires[String(a.id)] || null,
            })),
            victimes: [...autoVictimes, ...victimes],
            garanties: selectedGaranties,
            // Évaluation d'ouverture VENTILÉE par garantie (RC = barème auto, autres = libres) + total.
            evaluationsGaranties: evaluationsGarantiesPayload,
            evaluationInitiale: totalEvaluation,
            // Dossier transfrontalier (carte brune CEDEAO) → chèque au BNCB au règlement.
            carteBrune: !!formData?.carteBrune,
            heureSurvenance: formData?.heureSurvenance?.format('HH:mm') || null,
            // Date de déclaration : envoyée en LocalDate (YYYY-MM-DD). Null (ou sans habilitation
            // HSI100) → le backend retient la date de saisie, comme avant.
            dateDeclaration: peutSaisirDateDeclaration
                ? (formData?.dateDeclaration ? dayjs(formData.dateDeclaration).format('YYYY-MM-DD') : null)
                : null,
            photos: null, croquis: null,
            quartier: selectedLocation?.quartier?.nom,
            prefecture: selectedLocation?.prefecture?.nom,
            region: selectedLocation?.region?.nom,
            commune: selectedLocation?.commune?.nom,
            // Création automatique du quartier côté backend, rattaché aux entités géographiques.
            nouveauQuartier: selectedLocation?.quartier?.isNew || false,
            communeId: selectedLocation?.commune?.id ?? null,
            prefectureId: selectedLocation?.prefecture?.id ?? null,
            regionId: selectedLocation?.region?.id ?? null,
        };
        console.log('Données à envoyer :', dataObject);
        fd.append('data', new Blob([JSON.stringify(dataObject)], { type: 'application/json' }));
        const appendFiles = (files, key) => files?.fileList?.forEach(f => {
            if (f.originFileObj) fd.append(key, f.originFileObj, f.name);
            else if (f.file) fd.append(key, f.file, f.name);
        });
        appendFiles(photos, 'photos');
        appendFiles(croquis, 'croquis');

        axiosMultiPartDataInstance.post('/api/sinistre/web-new', fd)
            .then(response => {
                if (response.data) {
                    clearDraft(DRAFT_KEY); // déclaration enregistrée → plus de brouillon à conserver
                    navigate('/details-sinistre/v2', { replace: true, state: { codeId: response.data.codeId } });
                    Modal.success({ title: 'Déclaration enregistrée', content: 'Enregistrée avec succès !' });
                } else Modal.error({ title: 'Erreur', content: 'Une erreur est survenue.' });
            })
            .catch(e => Modal.error({ title: 'Erreur', content: e?.response?.data?.message || 'Une erreur est survenue.' }))
            .finally(() => setLoading(false));
    };

    // ── BROUILLON LOCAL (récupération après coupure/rechargement) ──────────────
    // Instantané sérialisable de toute la saisie (champs + états). Les pièces jointes
    // (photos/croquis) sont écartées par le sérialiseur et devront être re-jointes.
    const buildSnapshot = () => ({
        v: 1,
        savedAt: Date.now(),
        step: currentStep,
        fields: form.getFieldsValue(true),
        formData,
        state: {
            isAssureConducteur, adversaires, adversairePrincipalId, victimes,
            typeSinistre, selectedGaranties, evalParGarantie, garanties, engins,
            selectedContratObj, selectedLocation, contratsData, sequenceSinistre,
            periodesCouverture, autresCompagnies,
        },
    });

    // Y a-t-il une vraie saisie à sauvegarder/restaurer ? (évite de persister un formulaire vide)
    const draftHasContent = (d) => {
        if (!d) return false;
        const f = d.fields || {};
        const s = d.state || {};
        if (f.contratCodeId || f.enginId || f.dateSurvenance || f.lieuSinistre || f.descriptionSinistre) return true;
        if ((s.adversaires || []).length || (s.victimes || []).length || (s.selectedGaranties || []).length) return true;
        if ((d.step || 0) > 0) return true;
        return Object.keys(f).some((k) => {
            const v = f[k];
            return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
        });
    };

    const [brouillon, setBrouillon] = useState(null); // brouillon retrouvé au chargement (bandeau)
    const draftTimer = useRef(null);
    const hydratingRef = useRef(false); // true pendant la restauration → pas de re-sauvegarde parasite
    const mountedRef = useRef(false);
    const snapshotRef = useRef(buildSnapshot);
    snapshotRef.current = buildSnapshot; // toujours la dernière version (pour beforeunload)

    const persistDraft = () => {
        const snap = snapshotRef.current();
        if (draftHasContent(snap)) saveDraft(DRAFT_KEY, snap);
    };
    const scheduleSaveDraft = () => {
        if (hydratingRef.current) return;
        clearTimeout(draftTimer.current);
        draftTimer.current = setTimeout(persistDraft, 1200);
    };

    // 1) Au montage : s'il existe un brouillon récent exploitable, on l'expose au bandeau.
    useEffect(() => {
        const d = loadDraft(DRAFT_KEY, DRAFT_MAX_AGE);
        if (d && draftHasContent(d)) setBrouillon(d);
        mountedRef.current = true;
        return () => clearTimeout(draftTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 2) Sauvegarde débouncée à chaque changement d'état (les champs du Form passent par onValuesChange).
    useEffect(() => {
        if (mountedRef.current) scheduleSaveDraft();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, adversaires, adversairePrincipalId, victimes, typeSinistre, selectedGaranties,
        evalParGarantie, isAssureConducteur, selectedLocation, selectedContratObj, currentStep]);

    // 3) Filet de sécurité : sauvegarde immédiate à la fermeture/rechargement de l'onglet.
    useEffect(() => {
        const onLeave = () => { try { persistDraft(); } catch { /* ignore */ } };
        window.addEventListener('beforeunload', onLeave);
        return () => window.removeEventListener('beforeunload', onLeave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // « Reprendre » : ré-applique le brouillon à tout le formulaire.
    // IMPORTANT : les champs des étapes déjà validées sont dans `formData` (fusionnés à chaque `next()`),
    // tandis que `form.getFieldsValue(true)` ne capture que l'étape courante au moment de la sauvegarde.
    // On fusionne donc les deux, et on applique les valeurs APRÈS le rendu de l'étape cible (sinon les
    // Form.Item non encore montés sont ignorés par setFieldsValue).
    const reprendreBrouillon = () => {
        const snap = brouillon;
        if (!snap) return;
        hydratingRef.current = true;
        const merged = { ...(snap.formData || {}), ...(snap.fields || {}) };
        const s = snap.state || {};
        setFormData(merged);
        setIsAssureConducteur(s.isAssureConducteur ?? true);
        setAdversaires(s.adversaires || []);
        setAdversairePrincipalId(s.adversairePrincipalId ?? null);
        setVictimes(s.victimes || []);
        setTypeSinistre(s.typeSinistre || 'MATERIEL');
        setSelectedGaranties(s.selectedGaranties || []);
        setEvalParGarantie(s.evalParGarantie || {});
        if (s.garanties) setGaranties(s.garanties);
        if (s.engins) setEngins(s.engins);
        if (s.selectedContratObj) setSelectedContratObj(s.selectedContratObj);
        if (s.selectedLocation) setSelectedLocation(s.selectedLocation);
        if (s.contratsData) setContratsData(s.contratsData);
        if (s.sequenceSinistre) setSequenceSinistre(s.sequenceSinistre);
        if (s.periodesCouverture) setPeriodesCouverture(s.periodesCouverture);
        if (s.autresCompagnies) setAutresCompagnies(s.autresCompagnies);
        setCurrentStep(snap.step || 0);
        setBrouillon(null);
        // 1) remplir tout de suite (champs montés), 2) re-remplir après montage de l'étape cible.
        form.setFieldsValue(merged);
        setTimeout(() => {
            try { form.setFieldsValue(merged); } catch { /* ignore */ }
            hydratingRef.current = false;
        }, 0);
        message.success('Brouillon restauré. Pensez à re-joindre les pièces (constat, photos).');
    };

    // « Prévisualiser » : aperçu du contenu du brouillon sans rien restaurer.
    const previsualiserBrouillon = () => {
        const snap = brouillon;
        if (!snap) return;
        const f = { ...(snap.formData || {}), ...(snap.fields || {}) };
        const s = snap.state || {};
        const dte = (v) => (dayjs.isDayjs(v) ? v.format('DD/MM/YYYY') : (v || '—'));
        const hre = (v) => (dayjs.isDayjs(v) ? v.format('HH:mm') : (v || ''));
        const police = s.selectedContratObj?.numeroPoliceM || s.selectedContratObj?.numeroPoliceP || f.contratCodeId || '—';
        Modal.info({
            title: 'Aperçu du brouillon',
            width: 560,
            okText: 'Fermer',
            content: (
                <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                    <Descriptions.Item label="Sauvegardé">
                        {tempsEcoule(snap.savedAt)}{snap.savedAt ? ` · ${dayjs(snap.savedAt).format('DD/MM/YYYY HH:mm')}` : ''}
                    </Descriptions.Item>
                    <Descriptions.Item label="Étape atteinte">{(snap.step || 0) + 1} / {steps.length}</Descriptions.Item>
                    <Descriptions.Item label="Contrat">{police}</Descriptions.Item>
                    <Descriptions.Item label="Véhicule">{f.enginId || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Survenance">{dte(f.dateSurvenance)} {hre(f.heureSurvenance)}</Descriptions.Item>
                    <Descriptions.Item label="Lieu">{f.lieuSinistre || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Type">{s.typeSinistre || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Garanties">{(s.selectedGaranties || []).length}</Descriptions.Item>
                    <Descriptions.Item label="Adversaires / Victimes">
                        {(s.adversaires || []).length} / {(s.victimes || []).length}
                    </Descriptions.Item>
                </Descriptions>
            ),
        });
    };

    // « Ignorer » : on jette le brouillon et on repart d'un formulaire vierge.
    const ignorerBrouillon = () => { clearDraft(DRAFT_KEY); setBrouillon(null); };

    // Calcul auto selon le BARÈME (montant × nombre de victimes côté backend) → affecté à la RC,
    // pondéré par le taux de responsabilité. Les autres garanties gardent leur montant saisi.
    const calculateEvaluation = () => {
        const tauxResponsabilite = form.getFieldValue('tauxResponsabilite') ?? 100;
        const evalu = {
            nombreBlessesLegers: parseInt(form.getFieldValue('nbBlessesLegers') || 0),
            nombreBlessesMoyens: parseInt(form.getFieldValue('nbBlessesMoyens') || 0),
            nombreBlessesGraves: parseInt(form.getFieldValue('nbBlessesGraves') || 0),
            nombreDeces: parseInt(form.getFieldValue('nbDeces') || 0),
            typeDegats: form.getFieldValue('typeDegatsMateriels') === 'CORPOREL' ? null : form.getFieldValue('typeDegatsMateriels'),
        };
        axiosInstance.post('/api/bareme-sinistre/evaluation', evalu).then(res => {
            const rcValue = Math.round((Number(res.data) || 0) * (tauxResponsabilite / 100));
            setEvalParGarantie((prev) => {
                const next = { ...prev };
                const rcGar = garanties.find((g) => (selectedGaranties || []).includes(g.value) && isGarantieRC(g));
                if (rcGar) next[rcGar.value] = rcValue;
                return next;
            });
        }).catch(() => { });
    };

    // ── Adversaires ────────────────────────────────────────────────────────
    const ajouterAdversaire = async () => {
        try {
            const values = await adversaireForm.validateFields();
            let immatriculationComplete = '';
            if (typeAdversaire?.startsWith('VEHICULE')) {
                const { typeSerie: ts, matricule, numeroSerie } = values;
                immatriculationComplete = (ts === 'TG' && numeroSerie) ? `${ts}-${matricule}-${numeroSerie}` : `${matricule || ''}`;
            }
            const newAdv = { ...values, id: Date.now(), typeAdversaire, immatriculation: immatriculationComplete };
            const newList = [...adversaires, newAdv];
            setAdversaires(newList);
            if (newList.length === 1) setAdversairePrincipalId(newAdv.id);
            adversaireForm.resetFields();
            setShowAdversaireModal(false);
            setTypeAdversaireDommage(null);
        } catch { /* validation */ }
    };

    const supprimerAdversaire = (id) => {
        const remaining = adversaires.filter(a => a.id !== id);
        setAdversaires(remaining);
        if (adversairePrincipalId === id) setAdversairePrincipalId(remaining.length > 0 ? remaining[0].id : null);
    };

    const handleClick = () => {
        axiosInstance.get(`/api/contrat/codeId/${selectedContratObj?.codeId}`)
            .then(r => { setTheContract(r.data); setCModal(true); }).catch(() => { });
    };

    const steps = [
        { title: 'Informations sur le véhicule assuré', subtitle: 'Ajouter les infos du conducteur', icon: 'bx bx-user' },
        { title: 'Infos sur la(les) partie(s) adverse(s)', subtitle: 'Saisir les infos de la partie adverse', icon: 'bx bx-user-pin' },
        { title: "Informations sur l'accident", subtitle: 'Renseigner les infos sur l\'accident', icon: 'bx bxs-car-crash' },
        { title: 'Informations sur la(les) victime(s)', subtitle: 'Renseigner les infos sur les victimes', icon: 'bx bx-user-voice' },
        { title: 'Résumé de la déclaration', subtitle: 'Vérifier les informations', icon: 'bx bx-detail' },
    ];

    // ══════════════════════════════════════════════════════════════════════════
    // ÉTAPE 1 — Conducteur
    // ══════════════════════════════════════════════════════════════════════════
    const RenderStep1 = () => (
        <Card style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <div style={{ background: SINISTER_COLORS.primary, padding: '10px 30px', margin: '-24px -24px 24px -24px', borderRadius: '6px 6px 0 0' }}>
                <h2 style={{ color: '#fff', margin: 0, fontSize: 20, fontWeight: 'bold' }}>
                    <UserOutlined style={{ marginRight: 12 }} />Informations sur l'Assuré et le Véhicule
                </h2>
            </div>
            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item label={<b>Contrat</b>} name="contratCodeId" rules={[{ required: true, message: 'Sélectionnez un contrat' }]}>
                        <Select placeholder="Rechercher un contrat" showSearch filterOption={false} loading={contratsLoading}
                            onSearch={debouncedSearchContrats}
                            onChange={(_, obj) => {
                                setSelectedContratObj(obj.contrat);
                                setEngins(getEnginsFromContrat(obj.contrat));
                                // Indicatif : la même immatriculation a-t-elle un contrat VALIDE ailleurs ?
                                setAutresCompagnies([]);
                                if (obj.contrat?.immatriculation) {
                                    const norm = (s) => (s || '').toString().trim().toUpperCase();
                                    axiosInstance
                                        .get(`/api/sinistre/engin-autres-compagnies`, {
                                            params: { immatriculation: obj.contrat.immatriculation },
                                        })
                                        .then((res) => {
                                            const list = Array.isArray(res.data) ? res.data : [];
                                            setAutresCompagnies(
                                                list.filter((c) => norm(c.raisonSociale) !== norm(obj.contrat?.raisonSociale))
                                            );
                                        })
                                        .catch(() => setAutresCompagnies([]));
                                }
                                // Charge toutes les périodes de garantie (avenants) du contrat.
                                setPeriodesCouverture([]);
                                const cid = obj.contrat?.codeId;
                                if (cid) {
                                    axiosInstance.get(`/api/contrat/periodes-couverture/${cid}`)
                                        .then((res) => {
                                            setPeriodesCouverture(Array.isArray(res.data) ? res.data : []);
                                            form.validateFields(['dateSurvenance']).catch(() => { });
                                        })
                                        .catch(() => setPeriodesCouverture([]));
                                }
                            }}
                            notFoundContent={contratsLoading ? <Spin size="small" /> : 'Aucun contrat trouvé'}
                            options={contratsData} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item label={<b>Véhicule sinistré</b>} name="enginId" rules={[{ required: true, message: 'Sélectionnez un véhicule' }]}>
                        <Select placeholder="Sélectionner le véhicule" options={engins} />
                    </Form.Item>
                </Col>
            </Row>
            {autresCompagnies.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Ce véhicule a un contrat valide dans une autre compagnie"
                    description={
                        <List
                            size="small"
                            dataSource={autresCompagnies}
                            renderItem={(c) => (
                                <List.Item style={{ padding: '4px 0' }}>
                                    <Space wrap size={6}>
                                        <Tag color="volcano">{c.raisonSociale || 'Compagnie inconnue'}</Tag>
                                        <span>Police {c.numeroPolice || '—'}</span>
                                        {c.dateEcheance && (
                                            <span style={{ color: '#8c8c8c' }}>
                                                échéance {dayjs(c.dateEcheance).format('DD/MM/YYYY')}
                                            </span>
                                        )}
                                    </Space>
                                </List.Item>
                            )}
                        />
                    }
                />
            )}
            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item label={<b>Date de survenance</b>} name="dateSurvenance" rules={[
                        { required: true, message: 'Date requise' },
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const heure = form.getFieldValue('heureSurvenance');
                                const dt = value.clone();
                                if (heure) dt.hour(heure.hour()).minute(heure.minute());
                                if (dt.isAfter(new Date())) return Promise.reject('La date ne peut pas être dans le futur');

                                const d = dt.startOf('day');
                                // Union des périodes de garantie (tous les avenants) : un
                                // sinistre tardif tombant dans un avenant n-1 est accepté.
                                if (Array.isArray(periodesCouverture) && periodesCouverture.length > 0) {
                                    const couvert = periodesCouverture.some((p) =>
                                        p.dateEffet && p.dateEcheance &&
                                        !d.isBefore(dayjs(p.dateEffet).startOf('day')) &&
                                        !d.isAfter(dayjs(p.dateEcheance).startOf('day'))
                                    );
                                    if (!couvert) {
                                        const liste = periodesCouverture
                                            .filter((p) => p.dateEffet && p.dateEcheance)
                                            .map((p) => `${dayjs(p.dateEffet).format('DD/MM/YYYY')} → ${dayjs(p.dateEcheance).format('DD/MM/YYYY')}`)
                                            .join(' ; ');
                                        return Promise.reject(`Hors période de garantie. Périodes couvertes : ${liste}`);
                                    }
                                    return Promise.resolve();
                                }

                                // Repli (périodes non chargées) : période de la version sélectionnée.
                                const { dateEffet, dateEcheance } = selectedContratObj || {};
                                if (dateEffet && dateEcheance) {
                                    const e = dayjs(dateEffet).startOf('day'), ec = dayjs(dateEcheance).startOf('day');
                                    if (d.isBefore(e) || d.isAfter(ec)) return Promise.reject(`Entre le ${e.format('DD/MM/YYYY')} et le ${ec.format('DD/MM/YYYY')}`);
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}>
                        <DatePicker format="DD/MM/YYYY" disabledDate={c => c && c > new Date()} style={{ width: '100%' }} placeholder="Sélectionner la date" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item label={<b>Heure de survenance</b>} name="heureSurvenance" rules={[{ required: true, message: 'Heure requise' }]}>
                        <TimePicker style={{ width: '100%' }} format="HH:mm" placeholder="Sélectionner l'heure" />
                    </Form.Item>
                </Col>
                {/* Date de déclaration : saisissable UNIQUEMENT sous habilitation HSI100.
                    Sans le droit, le champ n'apparaît pas et le backend retient la date de saisie. */}
                {peutSaisirDateDeclaration && (
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={<b>Date de déclaration</b>}
                            name="dateDeclaration"
                            // Re-valide si la date de survenance change après coup (le contrôle
                            // « déclaration >= survenance » doit rester vrai dans les deux sens).
                            dependencies={['dateSurvenance']}
                            extra="Par défaut la date du jour. À modifier uniquement pour une déclaration antidatée."
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        if (value.startOf('day').isAfter(dayjs().startOf('day'))) {
                                            return Promise.reject('La date de déclaration ne peut pas être dans le futur');
                                        }
                                        const survenance = form.getFieldValue('dateSurvenance');
                                        if (survenance && value.startOf('day').isBefore(dayjs(survenance).startOf('day'))) {
                                            return Promise.reject('La déclaration ne peut pas précéder la survenance');
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <DatePicker
                                disabledDate={c => c && c > new Date()}
                                format="DD/MM/YYYY"
                                style={{ width: '100%' }}
                                placeholder="Date du jour par défaut"
                            />
                        </Form.Item>
                    </Col>
                )}
            </Row>
            <Form.Item label={<b>L'assuré était-il le conducteur ?</b>}>
                <Switch checked={isAssureConducteur} onChange={setIsAssureConducteur} checkedChildren="Oui" unCheckedChildren="Non" style={{ background: isAssureConducteur ? '#52c41a' : '#d9d9d9' }} />
            </Form.Item>
            <div style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', padding: 16, borderRadius: 6, marginTop: 16 }}>
                <h4 style={{ color: '#1565c0', marginBottom: 16 }}>
                    <CarOutlined style={{ marginRight: 8 }} />Informations sur le {isAssureConducteur ? "permis de l'assuré" : 'conducteur'}
                </h4>
                {!isAssureConducteur && (
                    <Row gutter={16}>
                        <Col xs={24} sm={12}><Form.Item label={<b>Nom du conducteur</b>} name={['conducteur', 'nom']} tooltip={TIP_INCONNU} rules={[{ required: true }]}><Input placeholder="Nom (ou INCONNU)" /></Form.Item></Col>
                        <Col xs={24} sm={12}><Form.Item label={<b>Prénom du conducteur</b>} name={['conducteur', 'prenom']} tooltip={TIP_INCONNU} rules={[{ required: true }]}><Input placeholder="Prénom (ou INCONNU)" /></Form.Item></Col>
                        <Col xs={24} sm={12}><Form.Item label={<b>Téléphone</b>} name={['conducteur', 'telephone']}><Input /></Form.Item></Col>
                        <Col xs={24} sm={12}><Form.Item label={<b>Adresse</b>} name={['conducteur', 'adresse']}><Input /></Form.Item></Col>
                    </Row>
                )}
                <Row gutter={16}>
                    <Col xs={24} sm={12}><Form.Item label={<b>Numéro de permis</b>} name={['permis', 'numero']}><Input placeholder="Numéro de permis" /></Form.Item></Col>
                    <Col xs={24} sm={12}><Form.Item label={<b>Catégorie de permis</b>} name={['permis', 'categorie']}><Select options={listeDesCategoriesDePermis} placeholder="Catégorie" /></Form.Item></Col>
                    <Col xs={24} sm={12}><Form.Item label={<b>Date d'obtention</b>} name={['permis', 'dateObtention']}><DatePicker format="DD/MM/YYYY" disabledDate={c => c && c > new Date()} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col xs={24} sm={12}><Form.Item label={<b>Lieu d'obtention</b>} name={['permis', 'lieuObtention']}><Select options={cityOptions} style={{ width: '100%' }} /></Form.Item></Col>
                </Row>
            </div>
        </Card>
    );

    // ══════════════════════════════════════════════════════════════════════════
    // ÉTAPE 2 — Adversaires avec principal
    // ══════════════════════════════════════════════════════════════════════════
    const RenderStep2 = () => (
        <>
            <Card style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <div style={{ background: SINISTER_COLORS.primary, padding: '10px 30px', margin: '-24px -24px 24px -24px', borderRadius: '6px 6px 0 0' }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <h2 style={{ color: '#fff', margin: 0, fontSize: 20, fontWeight: 'bold' }}>
                                <TeamOutlined style={{ marginRight: 12 }} />Adversaires impliqués
                            </h2>
                        </Col>
                        <Col>
                            <Badge count={adversaires.length} style={{ backgroundColor: '#52c41a' }}>
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAdversaireModal(true)} style={{ background: '#fff', color: SINISTER_COLORS.primary, border: 'none' }}>
                                    Ajouter un adversaire
                                </Button>
                            </Badge>
                        </Col>
                    </Row>
                </div>

                {/* Sélecteur adversaire principal (visible si > 1 adversaire) */}
                {adversaires.length > 1 && (
                    <div style={{ background: '#fff8e1', padding: '10px 16px', borderRadius: 8, marginBottom: 16, border: '1px solid #ffe082', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <span style={{ fontWeight: 600, color: '#e65100', whiteSpace: 'nowrap' }}>
                            <StarFilled style={{ color: '#ffc107', marginRight: 6 }} />Adversaire principal :
                        </span>
                        <Radio.Group value={adversairePrincipalId} onChange={e => setAdversairePrincipalId(e.target.value)}>
                            {adversaires.map((adv, i) => (
                                <Radio key={adv.id} value={adv.id}>
                                    {adv.immatriculation || adv.nom || `Adversaire ${i + 1}`}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </div>
                )}

                {adversaires.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <TeamOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
                        <p style={{ color: '#999', marginTop: 16 }}>Aucun adversaire ajouté</p>
                        <Button style={{ background: SINISTER_COLORS.primary }} type="primary" icon={<PlusOutlined />} onClick={() => setShowAdversaireModal(true)}>
                            Ajouter le premier adversaire
                        </Button>
                    </div>
                ) : (
                    <Row gutter={[16, 16]}>
                        {adversaires.map((adv, i) => {
                            const isPrincipal = adv.id === adversairePrincipalId;
                            return (
                                <Col xs={24} sm={12} md={8} key={adv.id}>
                                    <Card size="small" style={{ height: '100%', borderLeft: isPrincipal ? '4px solid #ffc107' : '4px solid #f5576c', boxShadow: isPrincipal ? '0 2px 12px rgba(255,193,7,0.3)' : '0 2px 8px rgba(0,0,0,0.08)', background: isPrincipal ? '#fffbf0' : '#fff' }}
                                        actions={[
                                            <Tooltip title={isPrincipal ? 'Adversaire principal' : 'Définir comme principal'}>
                                                <Button type="text" icon={isPrincipal ? <StarFilled style={{ color: '#ffc107' }} /> : <StarOutlined style={{ color: '#bbb' }} />}
                                                    onClick={() => setAdversairePrincipalId(isPrincipal ? null : adv.id)}>
                                                    {isPrincipal ? 'Principal' : 'Définir principal'}
                                                </Button>
                                            </Tooltip>,
                                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => supprimerAdversaire(adv.id)}>Retirer</Button>
                                        ]}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                                            <Avatar icon={adv.typeAdversaire?.startsWith('VEHICULE') ? <CarOutlined /> : <UserOutlined />}
                                                style={{ background: isPrincipal ? 'linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} />
                                            <div>
                                                <div style={{ fontWeight: 'bold' }}>
                                                    {isPrincipal && <StarFilled style={{ color: '#ffc107', marginRight: 4 }} />}
                                                    {adv.typeAdversaire === 'AUTRE' ? `${adv.description} – ` : ''}{adv.nom} {adv.prenom}
                                                </div>
                                                <Tag color={adv.typeAdversaire?.startsWith('VEHICULE') ? 'blue' : adv.typeAdversaire === 'PIETON' ? 'green' : 'orange'}>{adv.typeAdversaire}</Tag>
                                                {isPrincipal && <Tag color="gold">⭐ Principal</Tag>}
                                            </div>
                                        </div>
                                        {adv.immatriculation && <div style={{ fontSize: 13, color: '#666' }}><CarOutlined /> {adv.immatriculation}</div>}
                                        {adv.typeSinistre && <Tag color={TAG_DOMMAGE_COLOR[adv.typeSinistre] || 'default'} style={{ marginTop: 4 }}>{adv.typeSinistre}</Tag>}
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </Card>

            {/* ── Modal Adversaire ── */}
            <Modal title="Ajouter une partie Adverse" open={showAdversaireModal}
                onCancel={() => { adversaireForm.resetFields(); setShowAdversaireModal(false); setTypeSerie('TG'); setTypeAdversaireDommage(null); }}
                onOk={ajouterAdversaire} width={1100} okText="Ajouter" cancelText="Annuler" zIndex={2000} closable={false} maskClosable={false}>
                <Form form={adversaireForm} layout="vertical">
                    <Form.Item label="Type de partie adverse" name="typeAdversaire" initialValue="VEHICULE">
                        <Select style={{ width: '100%' }} onChange={v => { setTypeAdversaire(v); setTypeAdversaireDommage(null); }}>
                            <Option value="PIETON">🚶 Piéton</Option>
                            <Option value="VEHICULE">🚗 Véhiculé</Option>
                            <Option value="VEHICULE_MOTO">🏍️ Véhiculé – Moto (2 roues)</Option>
                            <Option value="VEHICULE_TRICYCLE">🛺 Véhiculé – Tricycle (3 roues)</Option>
                            <Option value="AUTRE">⚠️ Autre (Objet, Mur, etc.)</Option>
                        </Select>
                    </Form.Item>

                    {typeAdversaire === 'PIETON' && (
                        <Row gutter={16}>
                            <Col span={12}><Form.Item label="Nom" name="nom" rules={[{ required: true }]}><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item label="Prénom" name="prenom" rules={[{ required: true }]}><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item label="Téléphone" name="telephone"><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item label="Adresse" name="adresse"><Input /></Form.Item></Col>
                        </Row>
                    )}
                    {typeAdversaire === 'AUTRE' && (
                        <Row gutter={16}>
                            <Col span={24}><Form.Item label="Nom du propriétaire" name="nom" tooltip={TIP_INCONNU}><Input placeholder="Nom ou dénomination sociale (ou INCONNU)" /></Form.Item></Col>
                            <Col span={24}><Form.Item label="Description" name="description" tooltip={TIP_INCONNU} rules={[{ required: true }]}><Input placeholder="Ex: Mur, Poteau, Animal…" /></Form.Item></Col>
                        </Row>
                    )}

                    {typeAdversaire?.startsWith('VEHICULE') && (
                        <>
                            <Row gutter={16}>
                                <Col span={6}><Form.Item label="Nom" name="nom" tooltip={TIP_INCONNU}><Input placeholder="Nom (ou INCONNU)" /></Form.Item></Col>
                                <Col span={6}><Form.Item label="Prénom" name="prenom" tooltip={TIP_INCONNU}><Input placeholder="Prénom (ou INCONNU)" /></Form.Item></Col>
                                <Col span={6}><Form.Item label="Téléphone" name="telephone"><Input /></Form.Item></Col>
                                <Col span={6}><Form.Item label="Adresse" name="adresse"><Input /></Form.Item></Col>
                                <Col span={8}>
                                    <Form.Item label="Immatriculation" tooltip={TIP_INCONNU}>
                                        <Input.Group compact>
                                            <Form.Item name="typeSerie" noStyle initialValue="TG">
                                                <Select style={{ width: '25%' }} onChange={v => { setTypeSerie(v); if (v !== 'TG') adversaireForm.setFieldsValue({ numeroSerie: undefined }); }}>
                                                    <Option value="TG">TG</Option>
                                                    <Option value="AUTRE">Autre</Option>
                                                </Select>
                                            </Form.Item>
                                            <Form.Item name="matricule" noStyle rules={[{ required: true, message: 'Requis' }]}>
                                                <Input style={{ width: typeSerie === 'TG' ? '45%' : '75%' }} placeholder="Ex: 1234 AB" />
                                            </Form.Item>
                                            {typeSerie === 'TG' && (
                                                <Form.Item name="numeroSerie" noStyle rules={[{ required: true, message: 'Requis' }]}>
                                                    <Input style={{ width: '30%' }} placeholder="N° série" />
                                                </Form.Item>
                                            )}
                                        </Input.Group>
                                    </Form.Item>
                                </Col>
                                <Col span={4}><Form.Item label="Marque" name="marque"><Input /></Form.Item></Col>
                                <Col span={6}><Form.Item label="Nombre de personnes transportées" name="nombrePersonnesTransportees"><Input /></Form.Item></Col>
                                <Col span={4}><Form.Item label="Vitesse (km/h)" name="vitesse"><Input /></Form.Item></Col>
                                <Col span={12}><Form.Item label="Venant de" name="provenance"><Input /></Form.Item></Col>
                                <Col span={12}><Form.Item label="Se dirigeant vers" name="destination"><Input /></Form.Item></Col>
                                <Col span={12}>
                                    <Form.Item label="Type de dommages subis" name="typeSinistre">
                                        <Select style={{ width: '100%' }} onChange={setTypeAdversaireDommage} placeholder="Sélectionner">
                                            <Option value="MATERIEL">🔧 Matériel</Option>
                                            <Option value="CORPOREL">🏥 Corporel</Option>
                                            <Option value="MIXTE">⚠️ Mixte</Option>
                                            <Option value="AUCUN">✅ Aucun</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Société d'assurance adverse" name="societeAssurance">
                                        <Select style={{ width: '100%' }} options={assuranceOptions} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {(typeAdversaireDommage === 'MATERIEL' || typeAdversaireDommage === 'MIXTE') && (
                                <Form.Item label={<b>🔧 Description des dégâts matériels</b>} name="descriptionDegatsMateriels" rules={[{ required: true }]}>
                                    <TextArea rows={3} placeholder="Décrire les dommages matériels…" />
                                </Form.Item>
                            )}
                            {(typeAdversaireDommage === 'CORPOREL' || typeAdversaireDommage === 'MIXTE') && (
                                <Form.Item label={<b>🏥 Description des dommages corporels subis par le conducteur</b>} name="descriptionDegatsCorporels" rules={[{ required: true }]}>
                                    <TextArea rows={3} placeholder="Décrire les dommages corporels…" />
                                </Form.Item>
                            )}
                            {/* {typeAdversaireDommage === 'MIXTE' && (
                                <Form.Item label={<b>🔁 Ricochet – Dommages indirects</b>} name="ricochet">
                                    <TextArea rows={3} placeholder="Préciser les dommages indirects causés à des tiers…" />
                                </Form.Item>
                            )} */}

                            <Form.Item label={<b>La partie adverse était-elle le conducteur ?</b>}>
                                <Switch checked={isAdversaireConducteur} onChange={(v, e) => { e.stopPropagation(); setIsAdversaireConducteur(v); }} checkedChildren="Oui" unCheckedChildren="Non" style={{ background: isAdversaireConducteur ? '#52c41a' : '#d9d9d9' }} />
                            </Form.Item>
                            <div style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', padding: 16, borderRadius: 6, marginTop: 8 }}>
                                <h4 style={{ color: '#1565c0', marginBottom: 12 }}>
                                    <CarOutlined style={{ marginRight: 8 }} />Permis {isAdversaireConducteur ? 'de la partie adverse' : 'du conducteur adverse'}
                                </h4>
                                {!isAdversaireConducteur && (
                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}><Form.Item label={<b>Nom</b>} name={['conducteur', 'nom']} rules={[{ required: true }]}><Input /></Form.Item></Col>
                                        <Col xs={24} sm={12}><Form.Item label={<b>Prénom</b>} name={['conducteur', 'prenom']} rules={[{ required: true }]}><Input /></Form.Item></Col>
                                        <Col xs={24} sm={12}><Form.Item label={<b>Téléphone</b>} name={['conducteur', 'telephone']}><Input /></Form.Item></Col>
                                        <Col xs={24} sm={12}><Form.Item label={<b>Adresse</b>} name={['conducteur', 'adresse']}><Input /></Form.Item></Col>
                                    </Row>
                                )}
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}><Form.Item label={<b>N° permis</b>} name={['permis', 'numero']}><Input /></Form.Item></Col>
                                    <Col xs={24} sm={12}><Form.Item label={<b>Catégorie</b>} name={['permis', 'categorie']}><Select options={listeDesCategoriesDePermis} /></Form.Item></Col>
                                    <Col xs={24} sm={12}><Form.Item label={<b>Date d'obtention</b>} name={['permis', 'dateObtention']}><DatePicker format="DD/MM/YYYY" disabledDate={c => c && c > new Date()} style={{ width: '100%' }} /></Form.Item></Col>
                                    <Col xs={24} sm={12}><Form.Item label={<b>Lieu d'obtention</b>} name={['permis', 'lieuObtention']}><Select options={cityOptions} style={{ width: '100%' }} /></Form.Item></Col>
                                </Row>
                            </div>
                        </>
                    )}
                </Form>
            </Modal>
        </>
    );

    // ══════════════════════════════════════════════════════════════════════════
    // ÉTAPE 3 — Détails de l'accident (descriptions après observation, témoin compact)
    // ══════════════════════════════════════════════════════════════════════════
    const RenderStep3 = () => (
        <Card style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <div style={{ background: SINISTER_COLORS.primary, padding: '10px 30px', margin: '-24px -24px 24px -24px', borderRadius: '6px 6px 0 0' }}>
                <h2 style={{ color: '#fff', margin: 0, fontSize: 20, fontWeight: 'bold' }}>
                    <EnvironmentOutlined style={{ marginRight: 12 }} />Détails de l'Accident
                </h2>
            </div>

            <div style={{ background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', padding: 16, borderRadius: 12, marginBottom: 20 }}>
                <h4 style={{ color: '#e65100', marginBottom: 16 }}>📍 Localisation et Circonstances</h4>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<b>Quartier</b>} name="quartier"
                            help={selectedLocation?.type === 'nouveau-quartier'
                                ? (selectedLocation?.commune
                                    ? <span style={{ color: '#52c41a', fontSize: 12 }}>🆕 Nouveau quartier — {selectedLocation.commune.nom} – {selectedLocation.prefecture?.nom} – {selectedLocation.region?.nom}</span>
                                    : <span style={{ color: '#fa8c16', fontSize: 12 }}>🆕 Nouveau quartier — sélectionnez la commune de rattachement →</span>)
                                : (selectedLocation?.type === 'quartier' && selectedLocation?.commune
                                    ? <span style={{ color: '#52c41a', fontSize: 12 }}>📍 {selectedLocation.commune.nom} – {selectedLocation.prefecture?.nom} – {selectedLocation.region?.nom}</span>
                                    : null)}>
                            <Select placeholder="Rechercher ou saisir un quartier" onChange={handleQuartierSelect} onSearch={setQuartierSearch}
                                showSearch allowClear loading={loadingQuartiers} optionFilterProp="label"
                                notFoundContent={quartierSearch ? `Aucun résultat — sélectionnez « Créer le quartier » pour l'ajouter` : null}
                                onClear={() => { setSelectedLocation(null); setNewQuartierName(null); setQuartierSearch(''); form.setFieldsValue({ lieuSinistre: undefined, commune: undefined }); }}
                                options={(() => {
                                    const opts = quartiersData.map(q => ({ value: q.id, label: `${q.nom} (${q.commune?.nom})` }));
                                    const s = quartierSearch.trim();
                                    if (newQuartierName) opts.unshift({ value: `NEW::${newQuartierName}`, label: `🆕 ${newQuartierName} (nouveau quartier)` });
                                    if (s && !quartiersData.some(q => q.nom?.toLowerCase() === s.toLowerCase()) && s.toLowerCase() !== (newQuartierName || '').toLowerCase())
                                        opts.unshift({ value: `NEW::${s}`, label: `➕ Créer le quartier « ${s} »` });
                                    return opts;
                                })()}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<b>Commune (si quartier non disponible)</b>} name="commune"
                            rules={newQuartierName ? [{ required: true, message: 'Commune de rattachement requise pour le nouveau quartier' }] : []}>
                            <Select placeholder="Rechercher une commune" onChange={handleCommuneSelect} showSearch allowClear loading={loadingQuartiers}
                                disabled={!!form.getFieldValue('quartier') && !newQuartierName}
                                optionFilterProp="label"
                                onClear={() => {
                                    if (newQuartierName) setSelectedLocation({ type: 'nouveau-quartier', quartier: { nom: newQuartierName, isNew: true }, commune: null, prefecture: null, region: null });
                                    else { setSelectedLocation(null); form.setFieldsValue({ lieuSinistre: undefined }); }
                                }}
                                options={communesData.map(c => ({ value: c.id, label: `${c.nom} (${c.prefecture?.nom})` }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item label={<b>Lieu exact du sinistre</b>} name="lieu" rules={[{ required: true, message: 'Lieu requis' }]}>
                            <Input placeholder="Adresse exacte du sinistre" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12}><Form.Item label={<b>Provenance de l'assuré</b>} name="provenance"><Input placeholder="Lieu de départ" /></Form.Item></Col>
                    <Col xs={24} sm={12}><Form.Item label={<b>Destination de l'assuré</b>} name="destination"><Input placeholder="Lieu d'arrivée" /></Form.Item></Col>
                    <Col xs={24} sm={12}><Form.Item label={<b>Vitesse estimée (km/h)</b>} name="vitesse"><InputNumber min={0} max={200} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<b>Entité ayant fait le constat</b>} name="entiteConstat"
                            rules={[{ required: true, message: "L'entité de constat est obligatoire." }]}>
                            <Select showSearch allowClear style={{ width: '100%' }} placeholder="Sélectionner" options={entites} optionFilterProp="label"
                                filterOption={(i, o) => (o?.label ?? '').toLowerCase().includes((i ?? '').toLowerCase())} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<b>Type de dommages subis par l'assuré</b>} name="typeDommageAssure" rules={[{ required: true }]}>
                            <Select style={{ width: '100%' }} placeholder="Sélectionner">
                                <Option value="AUCUN">✅ Aucun dommage</Option>
                                <Option value="MATERIEL">🔧 Matériel uniquement</Option>
                                <Option value="CORPOREL">🏥 Corporel uniquement</Option>
                                <Option value="MIXTE">⚠️ Matériel et Corporel</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<b>Type de dommage général du sinistre</b>} name="typeSinistreGeneral" rules={[{ required: true }]}>
                            <Select style={{ width: '100%' }} placeholder="Sélectionner" onChange={setTypeSinistre}>
                                <Option value="MATERIEL">🔧 Matériel</Option>
                                <Option value="CORPOREL">🏥 Corporel</Option>
                                <Option value="MIXTE">⚠️ Mixte</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24}><Form.Item label={<b>Circonstances de l'accident</b>} name="circonstances" rules={[{ required: true }]}><TextArea rows={4} placeholder="Décrire comment l'accident s'est produit…" /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24}>
                        <Form.Item name="carteBrune" valuePropName="checked" style={{ marginBottom: 8 }}>
                            <Checkbox>
                                <b>Dossier transfrontalier (carte brune CEDEAO)</b>
                                <span style={{ color: '#888', marginLeft: 8, fontSize: 13 }}>
                                    — réglé sous la garantie CEDEAO ; le chèque sera libellé au Bureau National de la Carte Brune (BNCB).
                                </span>
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>

                {/* ── Position actuelle de chaque véhicule — un select par véhicule ── */}
                <div style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid #ffe0b2', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, color: '#bf360c', marginBottom: 12, fontSize: 14 }}>
                        🚗 Position actuelle de chaque véhicule
                    </div>
                    <Row gutter={16}>
                        {/* Véhicule assuré */}
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label={
                                    <span>
                                        <Tag color="blue" style={{ marginRight: 6 }}>Assuré</Tag>
                                        {formData?.enginId || 'Véhicule assuré'}
                                    </span>
                                }
                                name="positionVehiculeAssure"
                                rules={[{ required: true, message: 'Position requise' }]}
                            >
                                <Select placeholder="Position actuelle">
                                    <Option value="SUR_LES_LIEUX">📍 Sur les lieux</Option>
                                    <Option value="AU_GARAGE">🔧 Au garage</Option>
                                    <Option value="A_LA_FOURRIERE">🏛️ À la fourrière</Option>
                                    <Option value="DOMICILE_PROPRIETAIRE">🏠 Au domicile du propriétaire</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        {/* Un select par adversaire véhiculé */}
                        {adversaires.filter(a => a.typeAdversaire?.startsWith('VEHICULE')).map((adv, i) => (
                            <Col xs={24} sm={12} md={8} key={adv.id}>
                                <Form.Item
                                    label={
                                        <span>
                                            <Tag color={adv.id === adversairePrincipalId ? 'gold' : 'purple'} style={{ marginRight: 6 }}>
                                                {adv.id === adversairePrincipalId ? '⭐ Principal' : `Adverse ${i + 1}`}
                                            </Tag>
                                            {adv.immatriculation || adv.nom || `Adversaire ${i + 1}`}
                                        </span>
                                    }
                                    name={['positionVehiculesAdversaires', String(adv.id)]}
                                >
                                    <Select placeholder="Position actuelle">
                                        <Option value="SUR_LES_LIEUX">📍 Sur les lieux</Option>
                                        <Option value="AU_GARAGE">🔧 Au garage</Option>
                                        <Option value="A_LA_FOURRIERE">🏛️ À la fourrière</Option>
                                        <Option value="DOMICILE_PROPRIETAIRE">🏠 Au domicile du propriétaire</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        ))}
                        {/* Message si aucun adversaire véhiculé encore */}
                        {adversaires.filter(a => a.typeAdversaire?.startsWith('VEHICULE')).length === 0 && (
                            <Col xs={24} sm={12} md={16}>
                                <div style={{ color: '#999', fontSize: 13, paddingTop: 6, fontStyle: 'italic' }}>
                                    Les véhicules adverses apparaîtront ici une fois ajoutés dans l'onglet "Partie adverse".
                                </div>
                            </Col>
                        )}
                    </Row>
                </div>

                {/* Observation de l'agent AVANT les descriptions */}
                <Form.Item label={<b>Observation de l'agent</b>} name="observationEngin">
                    <TextArea rows={3} placeholder="État du véhicule assuré, défauts constatés…" />
                </Form.Item>

                {/* Descriptions des dégâts APRÈS l'observation de l'agent */}
                {(typeSinistre === 'MATERIEL' || typeSinistre === 'MIXTE') && (
                    <Form.Item label={<b>🔧 Description des dégâts matériels sur le véhicule assuré</b>} name="descriptionDegatsMateriels" rules={[{ required: true }]}>
                        <TextArea rows={4} placeholder="Décrire les dégâts matériels sur le véhicule assuré…" />
                    </Form.Item>
                )}
                {(typeSinistre === 'CORPOREL' || typeSinistre === 'MIXTE') && (
                    <Form.Item label={<b>🏥 Description des lésions corporelles subies par le conducteur du véhicule assuré</b>} name="descriptionDegatsCorporels" rules={[{ required: true }]}>
                        <TextArea rows={4} placeholder="Décrire les dégâts corporels sur le véhicule assuré…" />
                    </Form.Item>
                )}

                <Divider />
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<b>📋 Croquis de l'accident</b>} name="croquis">
                            <Upload listType="picture-card" maxCount={1}><div><UploadOutlined /><div style={{ marginTop: 8 }}>Croquis</div></div></Upload>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<b>📸 Photos de l'accident</b>} name="photos">
                            <Upload listType="picture-card" multiple maxCount={10}><div><UploadOutlined /><div style={{ marginTop: 8 }}>Photos</div></div></Upload>
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            {/* ── Témoin compact sur une seule ligne ── */}
            <div style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', padding: '14px 16px', borderRadius: 12 }}>
                <h4 style={{ color: '#2e7d32', marginBottom: 12 }}>👤 Témoin (Optionnel)</h4>
                <Row gutter={12} align="bottom">
                    <Col xs={24} sm={6}><Form.Item label={<b>Nom</b>} name={['temoin', 'nom']} style={{ marginBottom: 0 }}><Input placeholder="Nom" /></Form.Item></Col>
                    <Col xs={24} sm={6}><Form.Item label={<b>Prénom</b>} name={['temoin', 'prenom']} style={{ marginBottom: 0 }}><Input placeholder="Prénom" /></Form.Item></Col>
                    <Col xs={24} sm={6}><Form.Item label={<b>Téléphone</b>} name={['temoin', 'telephone']} style={{ marginBottom: 0 }}><Input placeholder="Téléphone" /></Form.Item></Col>
                    <Col xs={24} sm={6}><Form.Item label={<b>Adresse</b>} name={['temoin', 'adresse']} style={{ marginBottom: 0 }}><Input placeholder="Adresse" /></Form.Item></Col>
                </Row>
            </div>
        </Card>
    );

    // ══════════════════════════════════════════════════════════════════════════
    // ÉTAPE 4 — Victimes & Évaluation
    // ══════════════════════════════════════════════════════════════════════════
    const RenderStep4 = () => (
        <Card style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <div style={{ background: SINISTER_COLORS.primary, padding: '10px 30px', margin: '-24px -24px 24px -24px', borderRadius: '6px 6px 0 0' }}>
                <h2 style={{ color: '#fff', margin: 0, fontSize: 20, fontWeight: 'bold' }}>
                    <SafetyOutlined style={{ marginRight: 12 }} />Victimes & Évaluation
                </h2>
            </div>

            {/* Garanties */}
            <div style={{ background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)', padding: 16, borderRadius: 12, marginBottom: 20 }}>
                <h4 style={{ color: '#01579b', marginBottom: 16 }}>🛡️ Garanties affectées</h4>
                <Form.Item label={<b>Sélectionner les garanties affectées</b>} name="garanties">
                    <Checkbox.Group style={{ width: '100%' }} onChange={setSelectedGaranties}>
                        <Row gutter={[16, 16]}>
                            {garanties?.map(g => (
                                <Col xs={24} sm={12} key={g.value}>
                                    <Checkbox value={g.value} style={{ fontSize: 15 }}>
                                        <Tag color="blue" style={{ marginRight: 8 }}>{g.code}</Tag>
                                        {g.label.startsWith('RC') ? 'Responsabilité Civile' : g.label}
                                    </Checkbox>
                                </Col>
                            ))}
                        </Row>
                    </Checkbox.Group>
                </Form.Item>
            </div>

            {/* Bilan corporel */}
            {(typeSinistre === 'CORPOREL' || typeSinistre === 'MIXTE') && (
                <div style={{ background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', padding: 16, borderRadius: 12, marginBottom: 20 }}>
                    <h4 style={{ color: '#c62828', marginBottom: 16 }}>🏥 Bilan Corporel</h4>
                    <Row gutter={16}>
                        <Col xs={12} md={6}><Form.Item label={<b>Blessés légers</b>} name="nbBlessesLegers"><InputNumber min={0} style={{ width: '100%' }} onChange={calculateEvaluation} /></Form.Item></Col>
                        <Col xs={12} md={6}><Form.Item label={<b>Blessés moyens</b>} name="nbBlessesMoyens"><InputNumber min={0} style={{ width: '100%' }} onChange={calculateEvaluation} /></Form.Item></Col>
                        <Col xs={12} md={6}><Form.Item label={<b>Blessés graves</b>} name="nbBlessesGraves"><InputNumber min={0} style={{ width: '100%' }} onChange={calculateEvaluation} /></Form.Item></Col>
                        <Col xs={12} md={6}><Form.Item label={<b>Décès</b>} name="nbDeces"><InputNumber min={0} style={{ width: '100%' }} onChange={calculateEvaluation} /></Form.Item></Col>
                    </Row>
                </div>
            )}

            {/* Dégâts matériels */}
            {(typeSinistre === 'MATERIEL' || typeSinistre === 'MIXTE') && (
                <div style={{ background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', padding: 16, borderRadius: 12, marginBottom: 20 }}>
                    <h4 style={{ color: '#e65100', marginBottom: 16 }}>🔧 Dégâts Matériels</h4>
                    <Form.Item label={<b>Type de dégâts matériels</b>} name="typeDegatsMateriels">
                        <Select style={{ width: '100%' }} placeholder="Sélectionner" onChange={calculateEvaluation}>
                            <Option value="LEGERS">Dégâts légers / Sans précision</Option>
                            <Option value="MOYENS">Dégâts moyens</Option>
                            <Option value="IMPORTANTS">Dégâts importants</Option>
                            <Option value="EXTREMES">Cas extrêmes</Option>
                        </Select>
                    </Form.Item>
                </div>
            )}

            {/* Évaluation VENTILÉE PAR GARANTIE : la valeur auto (barème) alimente la RC, les autres
                garanties sélectionnées reçoivent un montant libre. Total = évaluation d'ouverture. */}
            <div style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', padding: 20, borderRadius: 12, marginBottom: 20 }}>
                <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col xs={24} sm={12}>
                        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>💰 Évaluation Initiale (totale)</div>
                        <div style={{ fontSize: 32, fontWeight: 'bold', color: '#1b5e20' }}>{displayNumbers(totalEvaluation)} FCFA</div>
                        <Button type="link" onClick={calculateEvaluation} style={{ padding: 0, marginTop: 8 }}>Recalculer automatiquement (RC selon barème)</Button>
                    </Col>
                    <Col xs={24} sm={12}>
                        <div style={{ fontSize: 13, color: '#33691e' }}>
                            La valeur calculée selon le barème (par victime) et le taux de responsabilité alimente la
                            <b> RC</b>. Saisissez librement l'évaluation des <b>autres garanties</b> sélectionnées.
                        </div>
                    </Col>
                </Row>

                {(selectedGaranties || []).length === 0 ? (
                    <div style={{ color: '#795548', fontStyle: 'italic' }}>
                        Sélectionnez au moins une garantie ci-dessus pour ventiler l'évaluation.
                    </div>
                ) : (
                    <Row gutter={[16, 12]}>
                        {(selectedGaranties || []).map((cid) => {
                            const g = garanties.find((x) => x.value === cid);
                            const rc = isGarantieRC(g);
                            const nom = g ? (estGarantieRC(g.label) ? 'Responsabilité Civile' : g.label) : cid;
                            return (
                                <Col xs={24} sm={12} key={cid}>
                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                        {g?.code ? <Tag color="blue">{g.code}</Tag> : null}{nom}
                                        {rc ? <Tag color="green" style={{ marginLeft: 6 }}>Auto (barème)</Tag> : null}
                                    </div>
                                    <InputNumber
                                        min={0}
                                        style={{ width: '100%' }}
                                        value={evalParGarantie[cid] ?? 0}
                                        onChange={(v) => setEvalParGarantie((prev) => ({ ...prev, [cid]: Number(v) || 0 }))}
                                        formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                        parser={v => `${v}`.replace(/\s?/g, '')}
                                        addonAfter="FCFA"
                                    />
                                </Col>
                            );
                        })}
                    </Row>
                )}
                {/* Champ caché : porte le total dans les valeurs du formulaire (aperçu / soumission). */}
                <Form.Item name="evaluationInitiale" hidden><InputNumber /></Form.Item>
            </div>

            <Divider />

            {/* ── VictimesSection avec auto-victimes ── */}
            <VictimesSection
                victimes={victimes}
                setVictimes={setVictimes}
                autoVictimes={autoVictimes}
                professions={professions}
                contrat={selectedContratObj}
                adversaires={adversaires}
            />
        </Card>
    );

    const RenderStep5 = () => (
        <PreviewFicheDeclaration
            sequenceSinistre={sequenceSinistre}
            selectedLocation={selectedLocation}
            isAssureConducteur={isAssureConducteur}
            sinistre={formData}
            adversaires={adversaires.map(a => ({ ...a, estPrincipal: a.id === adversairePrincipalId }))}
            garanties={selectedGaranties}
            victimes={[...autoVictimes, ...victimes]}
            contrat={selectedContratObj}
        />
    );

    // ══════════════════════════════════════════════════════════════════════════
    // RENDER PRINCIPAL
    // ══════════════════════════════════════════════════════════════════════════
    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu="SINISTRES" />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ padding: 24, minHeight: '100vh' }}>
                                <link href="https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css" rel="stylesheet" />
                                <style>{`
                                    .bs-stepper-header{display:flex;flex-direction:column;gap:0}
                                    .step{display:flex;flex-direction:column;align-items:stretch}
                                    .step-trigger{display:flex;align-items:center;gap:4px;background:none;border:none;padding:3px 3px 3px 12px;width:100%;text-align:left;cursor:pointer;border-radius:8px;transition:all .3s;position:relative}
                                    .step-trigger:hover{background:#f8f9fa}
                                    .step.active .step-trigger{background:${SINISTER_COLORS.primary}}
                                    .step.active .step-trigger::before{content:'';position:absolute;left:0;top:0;bottom:0;width:4px;background:#fff;border-radius:4px}
                                    .step.active .bs-stepper-circle{background:#fff;color:#667eea}
                                    .step.active .bs-stepper-title{color:#fff;font-weight:700}
                                    .step.active .bs-stepper-subtitle{color:rgba(255,255,255,.9)}
                                    .bs-stepper-circle{width:36px;height:36px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:#e9ecef;color:#6c757d;font-size:18px;transition:all .3s;flex-shrink:0}
                                    .bs-stepper-label{display:flex;flex-direction:column;gap:1px;flex:1}
                                    .bs-stepper-title{font-size:14px;font-weight:600;color:#333;line-height:1.3}
                                    .bs-stepper-subtitle{font-size:11px;color:#6c757d;line-height:1.3}
                                    .line{width:2px;height:12px;background:#e9ecef;margin-left:29px}
                                    .card{background:#fff;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,.08);padding:24px}
                                `}</style>

                                {brouillon && (
                                    <div style={{ maxWidth: 1400, margin: '0 auto 16px' }}>
                                        <Alert
                                            type="info"
                                            showIcon
                                            message="Brouillon non enregistré retrouvé"
                                            description={`Une déclaration en cours de saisie (${tempsEcoule(brouillon.savedAt)}) a été récupérée après le rechargement. Les pièces jointes (constat, photos) ne sont pas conservées et devront être re-jointes.`}
                                            action={
                                                <Space>
                                                    <Button size="small" onClick={previsualiserBrouillon}>Prévisualiser</Button>
                                                    <Button size="small" type="primary" onClick={reprendreBrouillon}>Reprendre</Button>
                                                    <Button size="small" onClick={ignorerBrouillon}>Ignorer</Button>
                                                </Space>
                                            }
                                        />
                                    </div>
                                )}

                                <ConfigProvider locale={frFR}>
                                    <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', gap: 24 }}>

                                        {/* Sidebar Steps */}
                                        <div style={{ width: 280, flexShrink: 0 }}>
                                            <div className="card" style={{ position: 'sticky', top: 24, padding: 16 }}>
                                                <div className="bs-stepper-header">
                                                    {steps.map((step, index) => (
                                                        <React.Fragment key={index}>
                                                            <div className={currentStep === index ? 'step active' : 'step'}>
                                                                <button type="button" className="step-trigger" onClick={() => setCurrentStep(index)}>
                                                                    <span className="bs-stepper-circle"><i className={step.icon} /></span>
                                                                    <span className="bs-stepper-label">
                                                                        <span className="bs-stepper-title">{step.title}</span>
                                                                        <span className="bs-stepper-subtitle">{step.subtitle}</span>
                                                                    </span>
                                                                </button>
                                                            </div>
                                                            {index < steps.length - 1 && <div className="line" />}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                                <div className="text-center" style={{ marginTop: 40 }}>
                                                    <Button type="primary" icon={<FilePdfOutlined />} onClick={handleClick} style={{ backgroundColor: 'gray' }} disabled={!selectedContratObj}>
                                                        Prévisualiser le contrat
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contenu principal */}
                                        <Col xs={24} md={18}>
                                            <Form form={form} layout="vertical" onValuesChange={scheduleSaveDraft}>
                                                {currentStep === 0 && RenderStep1()}
                                                {currentStep === 1 && RenderStep2()}
                                                {currentStep === 2 && RenderStep3()}
                                                {currentStep === 3 && RenderStep4()}
                                                {currentStep === 4 && RenderStep5()}
                                            </Form>

                                            <Card style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', marginTop: 24 }}>
                                                <div style={{ textAlign: 'right' }}>
                                                    <Space size="large">
                                                        {currentStep > 0 && <Button size="large" onClick={prev} style={{ minWidth: 120 }}>← Précédent</Button>}
                                                        {currentStep < steps.length - 1 && (
                                                            <Button type="primary" size="large" onClick={next} style={{ background: SINISTER_COLORS.primary, border: 'none', minWidth: 120 }}>Suivant →</Button>
                                                        )}
                                                        {currentStep === steps.length - 1 && (
                                                            <Button type="primary" size="large" onClick={handleSubmit} icon={<CheckCircleOutlined />} loading={loading}
                                                                style={{ background: SINISTER_COLORS.primary, border: 'none', minWidth: 180 }}>
                                                                Enregistrer la Déclaration
                                                            </Button>
                                                        )}
                                                    </Space>
                                                </div>
                                            </Card>
                                        </Col>
                                    </div>
                                </ConfigProvider>

                                <Modal title="Condition Particulière & Attestation" centered open={cModal} onOk={() => setCModal(false)} onCancel={() => setCModal(false)} width={1200} zIndex={2000}>
                                    <div id="myContrat">
                                        <div style={{ width: '210mm', height: '297mm' }}><DTRFFormat contrat={theContract} /></div>
                                        <div style={{ width: '297mm', height: '210mm' }}><AttestationMoto engin={theContract?.engins?.[0]} contrat={theContract} /></div>
                                    </div>
                                </Modal>
                            </div>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade" />
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle" />
            <div className="drag-target" />
        </div>
    );
};

export default DeclareSinistre;